import './Assistant.css';

import AddIcon from '@mui/icons-material/Add';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import { Alert, CircularProgress, Snackbar } from '@mui/material';
import { CustomDrawer } from 'components/Common/CustomDrawer/Index';
import React, { useEffect, useState } from 'react'; // Import useState for state management

// import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import { ScribeEditCircleIcon } from '../../components/Icons/ScribeIcons';
import AssistantAddForm from './AssistantAddForm';

interface Assistants {
  id: string;
  name: string;
  category: string;
  language: string;
  status: string;
  share_global: false;
  share_group: false;
  advanced_instructions: string;
  example1: string;
  example2: string;
}

const Assistant = () => {
  const user = JSON.parse(localStorage.getItem('independent') || '{}');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false); // State to manage drawer visibility
  const [assistants, setAssistants] = useState<Assistants[]>([]);
  const [error, setError] = useState<string | null>(null); // Error state for handling API errors
  const [selectedAssistant, setSelectedAssistant] = useState<Assistants | null>(
    null
  );
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [loading, setLoading] = useState<boolean>(false);

  const oauthToken = localStorage.getItem('AUTH_TOKEN') || '';

  if (!oauthToken) {
    throw new Error('OAuth token is missing');
  }

  const baseURL = process.env.REACT_APP_API_BASE_URL;

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const fetchAssistants = async () => {
    setError(null);
    setLoading(true); // Start loader

    try {
      const response = await fetch(
        `${baseURL}/api/instructions?userId=${user.id}&status=Active`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${oauthToken}`, // Adding the Bearer token to the headers
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch assistants');
      }

      const data = await response.json();
      setAssistants(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Failed to load assistants');
    } finally {
      setLoading(false); // Stop loader
    }
  };

  useEffect(() => {
    fetchAssistants();
  }, []);

  const handleButtonClick = () => {
    setIsDrawerOpen(true); // Open the drawer when the button is clicked
  };

  const handleViewButtonClick = (assistant: Assistants) => {
    setSelectedAssistant(assistant);
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false); // Close the drawer
    setSelectedAssistant(null);
  };

  if (error) {
    return <div>{error}</div>;
  }

  const updateGlobalData = (newGlobalData: any) => {
    setAssistants(newGlobalData); // Update the global data state
  };

  const handleDeactivateButtonClick = async (assistantId: string) => {
    try {
      // Make the API call to deactivate the assistant
      const response = await fetch(
        `${baseURL}/api/assistant-instructions/${assistantId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${oauthToken}`, // Adding the Bearer token to the headers
          },
          body: JSON.stringify({
            status: 'Inactive', // Set the status to "Inactive"
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to deactivate assistant');
      }

      fetchAssistants();
      setSnackbarMessage('Assistant has been deactivated successfully');
      setOpenSnackbar(true);
      // If no updated data is returned by the server, you can simply update the status in the state
    } catch (apiError: any) {
      alert('Failed to deactivate assistant');
      setSnackbarMessage('Failed to deactivate assistant');
      setOpenSnackbar(true);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <h3 className="assistant-heading">Assistant</h3>
        <button
          className="button bg-[#11AC9E] hover:bg-[#90BEE6]"
          onClick={handleButtonClick}
        >
          <AddIcon className="mr-2 h-5 w-5 fill-white" />
          New Assistant
        </button>
      </div>

      {/* Conditionally render the CustomDrawer based on isDrawerOpen */}
      {isDrawerOpen && (
        <CustomDrawer
          heading={selectedAssistant ? 'Edit Assistant' : 'New Assistant'}
          onOpenChange={handleCloseDrawer}
          width="50%"
        >
          <div className="pl-2">
            <AssistantAddForm
              closeForm={handleCloseDrawer}
              selectedAssistant={selectedAssistant}
              updateGlobalData={updateGlobalData}
              newCategory={null}
            />
          </div>
        </CustomDrawer>
      )}

      {loading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '150px',
          }}
        >
          <CircularProgress sx={{ color: '#90bee6' }} />
        </div>
      ) : (
        assistants.map((assistant) => (
          <div className="New-section" key={assistant.id}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex' }}>
                <h3 className="New-section-text">{assistant.name}</h3>
                <h6
                  className="assistant-text"
                  style={{
                    color:
                      assistant.category === 'letter' ||
                      assistant.category === 'Custom'
                        ? '#C11574'
                        : '#175CD3', // Set color based on category
                    paddingRight:
                      assistant.category === 'procedure' ? '19px' : '8px',
                  }}
                >
                  {assistant.category.charAt(0).toUpperCase() +
                    assistant.category.slice(1)}
                </h6>
                <h6 className="assistant-lan">
                  {assistant.language.charAt(0).toUpperCase() +
                    assistant.language.slice(1)}
                </h6>
              </div>
              <div style={{ marginRight: '2%', display: 'flex' }}>
                <button
                  style={{ border: 'none', display: 'flex' }}
                  onClick={() => handleViewButtonClick(assistant)}
                >
                  <div style={{ marginTop: '7px', marginRight: '8px' }}>
                    <ScribeEditCircleIcon />
                  </div>
                  <span className="view-new-button">Edit</span>
                </button>
                {/* Vertical Dot Divider */}
                <div
                  style={{
                    borderLeft: '1px dashed #D0D5DD', // Dash style and color
                    height: '30px', // Adjust the height of the line
                    margin: 'auto 20px', // Adjust spacing between the buttons and the divider
                  }}
                ></div>
                <button
                  style={{
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  onClick={() => handleDeactivateButtonClick(assistant.id)}
                >
                  <HighlightOffOutlinedIcon
                    style={{
                      width: '20px',
                      height: '20px',
                      marginRight: '8px',
                      fill: '#F93C3C',
                    }}
                  />
                  <span className="deactive">Deactivate</span>
                </button>
              </div>
            </div>
          </div>
        ))
      )}
      <Snackbar
        open={openSnackbar}
        onClose={handleCloseSnackbar}
        autoHideDuration={4000}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarMessage.includes('success') ? 'success' : 'error'}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Assistant;
